import {
  WSActions,
  WSElement,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import React, { useMemo } from "react";
import { Redirect, Route, RouteComponentProps, Switch } from "react-router-dom";
import { Tab, Tabs } from "../../../../components/Tabs";
import { Layout } from "../../../../shared/components/Layout";
import {
  CreateRequirementTabSlug,
  ENGAGEMENT_REQUIREMENTS_CREATE_BY_TEMPLATE_SELECT_PATH,
  ENGAGEMENT_REQUIREMENTS_ROOT_PATH
} from "../../paths/constants";
import {
  getCreateRequirementDefinitionFlowPathname,
  getCreateRequirementDefinitionPathname
} from "../../paths/helpers";
import { List } from "./components/List";
import { RequirementDefinitionTemplate } from "./types";
import { buildEngagementTypeDetailsURL } from "../../../EngagementTypes/paths";

export const RouteCreateRequirementByTemplate: React.FC<
  RouteComponentProps<{}, {}>
> = ({ location: { state }, history }) => {
  const [selectedTemplate, setSelectedTemplate] =
    React.useState<RequirementDefinitionTemplate | null>(null);

  const tabs: Tab[] = useMemo(
    () => [
      {
        label: "All",
        path: getCreateRequirementDefinitionPathname("all"),
        onClick() {
          setSelectedTemplate(null);
        }
      },
      {
        label: "Standard",
        path: getCreateRequirementDefinitionPathname("standard"),
        onClick() {
          setSelectedTemplate(null);
        }
      },
      {
        label: "Smart",
        path: getCreateRequirementDefinitionPathname("smart"),
        onClick() {
          setSelectedTemplate(null);
        }
      }
    ],
    []
  );

  const onClose = useMemo(
    () => () => {
      if (state?.engagementId) {
        history.push(buildEngagementTypeDetailsURL(state.engagementId));
      } else {
        history.push(ENGAGEMENT_REQUIREMENTS_ROOT_PATH);
      }
    },
    [history, state]
  );

  return (
    <Layout wide title="Create requirement" onClose={onClose}>
      <WSList gap="2XL">
        <WSElement>
          <WSText.Paragraph weight="medium" color="gray600">
            Requirement templates
          </WSText.Paragraph>
          <WSText.ParagraphSm color="gray500" mt="S">
            Choose from standard and smart requirement templates to get started.
          </WSText.ParagraphSm>
        </WSElement>

        <Tabs tabs={tabs} />

        <Switch>
          <Route
            path={ENGAGEMENT_REQUIREMENTS_CREATE_BY_TEMPLATE_SELECT_PATH}
            render={params => {
              const tabId = params.match.params
                .tabId as CreateRequirementTabSlug;

              const contents: Record<
                CreateRequirementTabSlug,
                React.ReactNode
              > = {
                all: (
                  <List
                    tiers={["Standard", "Smart", "SmartAdvanced"]}
                    onSelect={setSelectedTemplate}
                    selected={selectedTemplate}
                  />
                ),
                standard: (
                  <List
                    tiers={["Standard"]}
                    onSelect={setSelectedTemplate}
                    selected={selectedTemplate}
                  />
                ),
                smart: (
                  <List
                    tiers={["Smart", "SmartAdvanced"]}
                    onSelect={setSelectedTemplate}
                    selected={selectedTemplate}
                  />
                )
              };

              if (!contents[tabId]) {
                return (
                  <Redirect
                    to={getCreateRequirementDefinitionPathname("all")}
                  />
                );
              }

              return contents[tabId];
            }}
          />
        </Switch>

        <WSActions
          alignment="fill"
          buttons={[
            {
              label: "Cancel",
              onClick: onClose,
              kind: "Tertiary"
            },
            {
              label: "Continue",
              disabled: !selectedTemplate,
              kind: "Primary",
              onClick: () => {
                const engagementId = state?.engagementId;
                history.push(
                  getCreateRequirementDefinitionFlowPathname(
                    selectedTemplate!.key
                  ),
                  { engagementId }
                );
              }
            }
          ]}
        />
      </WSList>
    </Layout>
  );
};
